import { PrivateKey, BadKeyError } from '@hashgraph/sdk';
import { Buffer } from 'buffer';

let activeKey: PrivateKey | null = null;
let failedAttempts = 0;

export async function encryptToKeystore(privateKey: PrivateKey, password: string): Promise<string> {
  const keystoreBytes = await privateKey.toKeystore(password);
  activeKey = privateKey;
  return Buffer.from(keystoreBytes).toString('base64');
}

export async function decryptFromKeystore(
  base64Keystore: string,
  password: string,
  keyType?: 'ED25519' | 'ECDSA',
): Promise<PrivateKey> {
  if (failedAttempts >= 5) {
    const delaySec = Math.min(Math.pow(2, failedAttempts - 5), 8);
    await new Promise(resolve => setTimeout(resolve, delaySec * 1000));
  }

  try {
    const bytes = new Uint8Array(Buffer.from(base64Keystore, 'base64'));
    let key = await PrivateKey.fromKeystore(bytes, password);

    // fromKeystore loses key type info — re-interpret as ECDSA if needed
    if (keyType === 'ECDSA') {
      const rawHex = key.toStringRaw();
      key = PrivateKey.fromStringECDSA(rawHex);
    }

    activeKey = key;
    failedAttempts = 0;
    return key;
  } catch (err) {
    if (err instanceof BadKeyError) {
      failedAttempts++;
      throw err;
    }
    throw err;
  }
}

export async function changePassword(
  base64Keystore: string,
  oldPassword: string,
  newPassword: string,
  keyType?: 'ED25519' | 'ECDSA',
): Promise<string> {
  const key = await decryptFromKeystore(base64Keystore, oldPassword, keyType);
  return encryptToKeystore(key, newPassword);
}

export function clearKey(): void {
  activeKey = null;
}

export function getActiveKey(): PrivateKey | null {
  return activeKey;
}

export function isUnlocked(): boolean {
  return activeKey !== null;
}

export function resetFailedAttempts(): void {
  failedAttempts = 0;
}

export { BadKeyError };
